import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const FINNISH_LOCALE = 'fi-FI';
const FINNISH_TIME_ZONE = 'Europe/Helsinki';

// import { format } from 'date-fns-tz';
/*
const formatUtcToLocal = (utcDateString: string, locale: string = 'fi-FI') => {
  if (!utcDateString) return null;

  // Parse the ISO string into a Date object
  const utcDate = parseISO(utcDateString);

  // Format the date in the user's local timezone
  return format(utcDate, 'dd.MM.yyyy HH:mm:ss', {
    locale,
  });
};
*/
/**
 * Compares two dates ignoring the time portion.
 * @returns -1 if date1 is before date2, 0 if equal, 1 if date1 is after date2
 */
export function compareDatesIgnoringTime(date1: Date, date2: Date): number {
  // Create new Date objects with only the date part
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

  // Compare timestamps
  if (d1.getTime() < d2.getTime()) return -1;
  if (d1.getTime() > d2.getTime()) return 1;
  return 0;
}

/**
 * Checks if date1 is less than date2, ignoring time.
 */
export function isDateLessThanIgnoringTime(date1: Date, date2: Date): boolean {
  return compareDatesIgnoringTime(date1, date2) < 0;
}

/**
 * Checks if date1 is greater than date2, ignoring time.
 */
export function isDateGreaterThanIgnoringTime(
  date1: Date,
  date2: Date,
): boolean {
  return compareDatesIgnoringTime(date1, date2) > 0;
}

/**
 * Checks if date1 is less than or equal to date2, ignoring time.
 */
export function isDateLessThanOrEqualIgnoringTime(
  date1: Date,
  date2: Date,
): boolean {
  return compareDatesIgnoringTime(date1, date2) <= 0;
}

/**
 * Checks if date1 is greater than or equal to date2, ignoring time.
 */
export function isDateGreaterThanOrEqualIgnoringTime(
  date1: Date,
  date2: Date,
): boolean {
  return compareDatesIgnoringTime(date1, date2) >= 0;
}
export const isValidDate = date => {
  return (
    date !== null &&
    date !== undefined &&
    !Number.isNaN(new Date(date).getTime())
  );
};

export const equalsMatcher = (date1: Date, date2: Date) => {
  return (
    date1 !== null &&
    date1 !== undefined &&
    date2 !== null &&
    date2 !== undefined &&
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const formatDateToFinnish = (date: Date): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

export const formatDateToFinnishDisplay = (date: Date): string => {
  return format(date, "dd.MM.yyy', Klo:'HH:mm:ss");
};

export const formatDateToYYYYMMDDWithoutTime = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

export const formatIsoDateToDateOnlyString = (dateString: string): string => {
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${day}.${month}.${year}`;
};

export function dateToDateOnlyString(date: Date | string) {
  // Convert to Date if it's not already an instance of Date
  const parsedDate = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsedDate);
}
export function JsDateToString(
  date: Date,
  showDay: boolean,
  showMonth: boolean,
  showYear: boolean,
  timeSeparator: string,
  showHours: boolean,
  showMinutes: boolean,
) {
  if (date == null) {
    return '';
  }
  const parsedDate = date instanceof Date ? date : new Date(date);
  // Extract date components
  const day = parsedDate.getUTCDate();
  const month = parsedDate.getUTCMonth() + 1; // Months are zero-based, so add 1
  const year = parsedDate.getUTCFullYear();
  const hours = parsedDate.getHours();
  const minutes = parsedDate.getMinutes().toString().padStart(2, '0'); // Ensure two-digit minutes
  let returnableString: string = '';
  // Format the components into the desired string
  if (showDay) {
    returnableString = day.toString();
  }
  if (showMonth) {
    if (returnableString !== '') {
      returnableString += '.';
    }
    returnableString += month.toString();
  }
  if (showYear) {
    if (returnableString !== '') {
      returnableString += '.';
    }
    returnableString += year.toString();
  }
  if (showHours || showMinutes) {
    returnableString += timeSeparator;
  }

  if (showHours) {
    returnableString += `${hours.toString()}:`;
  }
  if (showMinutes) {
    returnableString += minutes.toString();
  }
  return returnableString;
  // const formattedDate = `${day}.${month}.${year} klo ${hours}:${minutes}`;
}

export function convertJsDateToApiDateFinnishTimeString(date: Date) {
  // Format it to ISO 8601 in Finnish local time without the Z or offsets
  const finnishDate = date
    .toLocaleString('sv-SE', {
      timeZone: 'Europe/Helsinki',
      hour12: false,
    })
    .replace(' ', 'T');
  return finnishDate;
}

export const formatDateTimeFinnish = (date: Date | string): string => {
  if (!date) return '';
  const dt = date instanceof Date ? date : parseISO(String(date));
  // If parseISO fails (invalid), fallback to native Date parsing
  const valid = !Number.isNaN(dt.getTime()) ? dt : new Date(String(date));
  if (Number.isNaN(valid.getTime())) return String(date);
  return format(valid, 'dd.MM.yyyy HH:mm');
};

export const formatUtcDateTimeToFinnish = (date: Date | string): string => {
  if (!date) return '';
  const dt = date instanceof Date ? date : parseISO(String(date));
  // If parseISO fails (invalid), fallback to native Date parsing
  const valid = !Number.isNaN(dt.getTime()) ? dt : new Date(String(date));
  if (Number.isNaN(valid.getTime())) return String(date);

  // Format in Finnish timezone
  return formatInTimeZone(valid, 'Europe/Helsinki', 'dd.MM.yyyy HH:mm');
};

export const formatUtcAsFinnishDateTime = (value?: string | null): string => {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  const datePart = parsed.toLocaleDateString(FINNISH_LOCALE, {
    timeZone: FINNISH_TIME_ZONE,
  });

  const timeParts = new Intl.DateTimeFormat(FINNISH_LOCALE, {
    timeZone: FINNISH_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(parsed);

  const hours = timeParts.find(part => part.type === 'hour')?.value ?? '00';
  const minutes = timeParts.find(part => part.type === 'minute')?.value ?? '00';

  return `${datePart} ${hours}:${minutes}`;
};
